import { format, subDays } from 'date-fns'
import { addMonths, setDate, startOfDay, startOfMonth } from 'date-fns/esm'
import { orderBy } from 'lodash-es'
import { makeAutoObservable, observable, runInAction } from 'mobx'

import { paymentController } from '@/controller/payment.controller'
import type {
  Academy,
  AcademyPayment,
  PaymentMessageStatus,
  PaymentStudent,
  StudentPayment,
} from '@/dto'
import { ReqParams } from '@/dto'
import type { PageParamT, Sorts } from '@/dto/common/ReqParams'
import { USAGE_CATEGORY } from '@/page/etc/MyPage/MyStatus/UsageStatus/usageStatusPolicy'
import commonRepo from '@/repository/common.repo'
import { commonErrorMessage } from '@/utils/toast'

export class PaymentService {
  private _academyPayment: AcademyPayment | null = null
  private _isLoadingPayments = false
  private _academyStudentPaymentStatus: StudentPayment | null = null
  paymentMessageStatus?: PaymentMessageStatus // 결제예상금액 - 문자요금 건수, 문자요금
  private _filteringDate
  private _studentSort: 'desc' | 'asc' = 'desc'

  studentReqParams = new ReqParams<{ sorts: Sorts } & PageParamT>({
    params: { sorts: [['createDatetime', 'DESC']] },
  })
  messageReqParams = new ReqParams<{ sorts: Sorts } & PageParamT>()

  get 조회_날짜() {
    return this._filteringDate
  }

  // 신규 이용 현황 화면용 청구 정보. null 이면 그 달은 기존(결제 예상 금액) 화면으로 그린다.
  get billing() {
    return this._academyPayment?.billing ?? null
  }

  get 사용_시작일() {
    return this._academyPayment?.invoiceStartDatetime
  }

  get 사용_종료일() {
    return this._academyPayment?.invoiceEndDatetime
  }

  get 사용_기준일() {
    return this._academyPayment?.paymentDate || new Date()
  }

  get 사용기간() {
    const start = this.formatDatetime(this._academyPayment?.invoiceStartDatetime)
    const end = this.formatDatetime(this._academyPayment?.invoiceEndDatetime)

    return `${start} ~ ${end}`
  }

  get 최초_결제일() {
    if (!commonRepo.academy?.payStartDate) {
      return new Date()
    }
    const startDate = new Date(commonRepo.academy.payStartDate)
    return startDate
  }

  get 최종_결제일() {
    if (commonRepo.academy?.payEndDate) {
      return new Date(commonRepo.academy.payEndDate)
    }

    const today = startOfDay(new Date())
    const dayOfPay = commonRepo.academy?.payDate
    if (!dayOfPay) {
      return today
    }

    const thisMonthPayDate = setDate(today, dayOfPay)
    const periodEndDate = subDays(thisMonthPayDate, 4) // 사용기간은 결제일 - 4일

    if (today > periodEndDate) {
      return addMonths(thisMonthPayDate, 1)
    }
    return thisMonthPayDate
  }

  get yearMonthParam() {
    return {
      year: this._filteringDate.getFullYear().toString(),
      month: format(this._filteringDate, 'MM'),
    }
  }

  get 요금제기준() {
    return this.선택월_사용일_과금기준_여부 ? '사용일' : '사용량'
  }

  // 청구서가 있는 달은 학생 수 과금 조건이 청구 항목에만 있다. 상위 additionalPricingPlan* 는
  // 청구서 전환 학원에서 null 이거나 청구서와 다른 값이라 섞어 쓰지 않는다.
  private get _학생수_과금_단가() {
    // 그 달에 플랜을 바꿔 청구서가 여러 건이어도 학생 수 기준은 최신 청구서 하나로 통일한다.
    // 서버가 최신순으로 준다.
    const [최신_청구서] = this.billing?.billings ?? []

    return (
      최신_청구서?.usages.find((usage) => usage.category === USAGE_CATEGORY.PER_STUDENT)?.pricing ??
      null
    )
  }

  private get _과금_기준값() {
    if (this.billing) return this._학생수_과금_단가?.studentCountRule?.threshold ?? null

    return this._academyPayment?.additionalPricingPlanStandardValue ?? null
  }

  get 선택월_사용일_과금기준_여부() {
    if (this.billing) {
      return this._학생수_과금_단가?.studentCountRule?.criterion === 'NUMBER_OF_ACTIVE_DAYS'
    }

    return this.checkAdditionalPricingPlan(this._academyPayment?.additionalPricingPlan) === '사용일'
  }

  get 사용일_과금기준_여부() {
    return this.checkAdditionalPricingPlan(commonRepo.academy?.additionalPricingPlan) === '사용일'
  }

  get 결제_기준일() {
    const paymentDate = this._academyPayment?.paymentDate
    return paymentDate?.getDate() || '-'
  }

  get 추가_요금() {
    return (this._academyPayment?.additionalFee || 0).toLocaleString()
  }

  get hwp_다운로드_요금() {
    return (this._academyPayment?.additionalHwpFee || 0).toLocaleString()
  }

  get hwp_다운로드_문항수() {
    return (this._academyPayment?.hwpProblemCount || 0).toLocaleString()
  }

  get 기본_요금_포함_학생수() {
    const 무료_학생수 = this.billing
      ? this._학생수_과금_단가?.freeStudentCount
      : this._academyPayment?.additionalPricingPlanFreeStudentCount

    return (무료_학생수 ?? 0).toLocaleString()
  }

  get 미수금() {
    return (this._academyPayment?.debt || 0).toLocaleString()
  }

  get 기본요금() {
    return (this._academyPayment?.subscribeFee || 0).toLocaleString()
  }

  get 추가_과금_구분() {
    return this._academyPayment?.additionalPricingPlan
  }

  get 무제한_요금제_여부() {
    return this._academyPayment?.subscribePlan?.includes('UNLIMITED') || false
  }

  get 프리미엄_요금제_여부() {
    return this._academyPayment?.subscribePlan?.includes('PREMIUM') || false
  }

  get localeStringTotalPayment(): string {
    return (
      (this._academyPayment?.paymentAmountExceptMessageFee || 0) +
      (this.paymentMessageStatus?.totalFee || 0)
    ).toLocaleString()
  }

  get 사용_학생_현황() {
    if (this.선택월_사용일_과금기준_여부) {
      return this.filterStudentPaymentList(
        (student) => student.activeDateCount >= 1,
        'activeDateCount',
        this._studentSort,
      )
    }

    return this.filterStudentPaymentList(
      (student) => student.solveCount >= 1,
      'solveCount',
      this._studentSort,
    )
  }

  get 사용량_정렬_기준() {
    return this._studentSort
  }

  get 과금_학생_목록() {
    if (this.무제한_요금제_여부) {
      return []
    }

    if (this.선택월_사용일_과금기준_여부) {
      return this.filterStudentPaymentList(
        (student) => student.activeDateCount >= (this._과금_기준값 || 4),
        'activeDateCount',
        this._studentSort,
      )
    }

    return this.filterStudentPaymentList(
      (student) => student.solveCount >= (this._과금_기준값 || 1),
      'solveCount',
      this._studentSort,
    )
  }

  get 과금_기준에_따른_조건() {
    return (this._과금_기준값 ?? 0).toLocaleString()
  }

  get 기간내_추가_과금_학생수() {
    return (this._academyPayment?.additionalStudentCount || 0).toLocaleString()
  }

  get 과금_대상_학생_노출문구() {
    if (this.무제한_요금제_여부) {
      return null
    }

    return this.선택월_사용일_과금기준_여부 ? (
      <span>
        사용기간(한 달 기준) 내 “{commonRepo.isSchoolflat ? '사용' : '재원'}”을{' '}
        {this.과금_기준에_따른_조건}일 이상 유지한 학생입니다.
      </span>
    ) : (
      <span>
        사용기간(한 달 기준) 내 “채점”을 한 문제 수가 {this.과금_기준에_따른_조건}개 이상인
        학생입니다.
      </span>
    )
  }

  get 사용자_노출_요금제명() {
    switch (this._academyPayment?.subscribePlan) {
      case 'BASIC':
      case 'NEW_BASIC':
        return '베이직'
      case 'BASIC_PLUS':
      case 'NEW_BASIC_PLUS':
        return '베이직 플러스'
      case 'BASIC_UNLIMITED':
        return '베이직 무제한'
      case 'BASIC_CUSTOM':
      case 'NEW_BASIC_CUSTOM':
        return '베이직 커스텀'
      case 'NEW_BASIC_SPECIAL':
        return '베이직 스페셜'
      case 'NEW_BASIC_PERSONAL':
        return '베이직 퍼스널'
      case 'PREMIUM':
        return '프리미엄'
      case 'PREMIUM_PLUS':
        return '프리미엄 플러스'
      case 'PREMIUM_UNLIMITED':
        return '프리미엄 무제한'
      case 'PREMIUM_CUSTOM':
        return '프리미엄 커스텀'
      case 'SOLO':
        return '솔로'
      case 'SOLO_PLUS':
        return '솔로 플러스'
      default:
        return '-'
    }
  }

  get formattedStartYearMonth() {
    const [year, month] = commonRepo.academy?.payStartDate?.split('-') || []

    return startOfMonth(new Date(Number(year), Number(month) - 1))
  }

  constructor() {
    this._filteringDate = this.getInitialFilteringDate()

    makeAutoObservable<
      this,
      '_academyPayment' | '_academyStudentPaymentStatus' | '_filteringDate' | '_isLoadingPayments'
    >(this, {
      _academyPayment: observable,
      _isLoadingPayments: observable,
      _academyStudentPaymentStatus: observable,
      _filteringDate: observable,
    })

    // this.updateFilterDate(기본결제예상금액조회날짜)

    this.studentReqParams.excludeParams.add('size')
    this.studentReqParams.excludeParams.add('page')

    this.loadPayments()
  }

  getInitialFilteringDate() {
    return startOfDay(new Date())
  }

  updateFilterDate = (date: Date) => {
    this._filteringDate = date
    this._studentSort = 'desc'
    this.studentReqParams.resetPage()
  }

  updateStudentSort = (sort: 'desc' | 'asc') => {
    this._studentSort = sort
  }

  async fetchInitPayment() {
    try {
      const academyPayment = await paymentController.fetchAcademyPayment(this.yearMonthParam)
      runInAction(() => {
        this._academyPayment = academyPayment
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  async fetchPaymentStudent() {
    try {
      const academyStudentPaymentStatus = await paymentController.fetchAcademyStudentPayments(
        this.yearMonthParam,
      )
      runInAction(() => {
        this._academyStudentPaymentStatus = academyStudentPaymentStatus
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  async fetchPaymentMessage() {
    try {
      const { paymentMessageStatus } = await paymentController.fetchPaymentMessage(
        this.messageReqParams.toRequestParams,
        this.yearMonthParam,
      )
      runInAction(() => {
        this.paymentMessageStatus = paymentMessageStatus
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  fetchExpectTotalPaymentPrice(결제예상금액조회날짜: Date) {
    // 조회월과 로딩 표시를 한 액션으로 묶는다. 따로 갱신하면 그 사이 한 프레임이
    // 새 월 + 직전 달 금액 조합으로 그려진다.
    runInAction(() => {
      this._isLoadingPayments = true
      this.updateFilterDate(결제예상금액조회날짜)
    })
    this.loadPayments()
  }

  // 조회가 끝날 때까지 이전 달 응답이 그대로 남아 있어, 화면이 옛 금액을 보여주지 않도록 알린다.
  get isLoadingPayments() {
    return this._isLoadingPayments
  }

  loadPayments() {
    ;(async () => {
      runInAction(() => {
        this._isLoadingPayments = true
        // 조회 실패 시 예외를 삼키고 이전 달 응답이 남아, 새 월 라벨과 옛 금액이 함께 노출됐다.
        this._academyPayment = null
      })
      try {
        await this.fetchInitPayment()
        await this.fetchPaymentStudent()
        await this.fetchPaymentMessage()
      } finally {
        runInAction(() => {
          this._isLoadingPayments = false
        })
      }
    })()
  }

  private filterStudentPaymentList(
    filter: (student: PaymentStudent) => boolean,
    additionalPricingValue: 'activeDateCount' | 'solveCount',
    sortOfAdditionalPricingValue: 'desc' | 'asc' = 'desc',
  ) {
    const studentPaymentList = this._academyStudentPaymentStatus?.studentPaymentList || []
    const filteredList = studentPaymentList.filter(filter)
    const studentPaymentListCopy = orderBy(
      filteredList,
      [
        (student) => student[additionalPricingValue],
        (student) => student.schoolGrade.flatGrade,
        (student) => student.studentName,
      ],
      [`${sortOfAdditionalPricingValue}`, 'asc', 'asc'],
    )
    return studentPaymentListCopy
  }

  private formatDatetime(datetime: Date | undefined) {
    if (!datetime) return '-'
    return format(datetime, 'yyyy.MM.dd.')
  }

  private checkAdditionalPricingPlan(additionalPricingPlan: Academy['additionalPricingPlan']) {
    if (additionalPricingPlan === 'NUMBER_OF_SOLVED_PROBLEM') {
      return '채점수'
    }
    return '사용일'
  }

  // private checkPlanCondition(subscribePlan: PlanNameValue | undefined): '기존' | '신규' {
  //   switch (subscribePlan) {
  //     case 'BASIC':
  //     case 'BASIC_PLUS':
  //     case 'BASIC_CUSTOM':
  //     case 'BASIC_UNLIMITED':
  //       return '기존'
  //     default:
  //       return '신규'
  //   }
  // }
}
